import * as THREE from 'three';

// Loads the 70s ballroom tools/build-ballroom.mjs cut into tiles - the room
// with its floor off, the light every tile adds (diff), the lossless id map,
// the per-tile data with its grid indices, and the depth and normal maps the
// mirror ball throws its spots on - once, at startup, for
// visualizers/ballroom.js. Same preload/get shape as alley-assets.js, and
// the same rule: the preload is a WIRING POINT every entry point must call
// (main, main-radio, main-embedded, play-boot, landing-boot, demo-boot, the
// capture harness); a forgotten one is silent. The preload resolves to null
// when a photograph is missing (the build then wrote no manifest), and the
// entry point withdraws the visualizer (disableVisualization).
//
// THE ID MAP IS DATA, NOT A PICTURE: NearestFilter, no mipmaps, no colour
// space. The depth and the normal are read back on the CPU (a canvas) as
// well: the spots are ray-marched in JavaScript, three hundred rays a frame.
const MANIFEST_URL = 'textures/ballroom.json';

let _assets = null;
let _promise = null;

function loadTexture(url, configure) {
  return new Promise(function (resolve, reject) {
    new THREE.TextureLoader().load(url, function (t) { configure(t); t.needsUpdate = true; resolve(t); }, undefined, function () {
      reject(new Error(url + ' failed to load'));
    });
  });
}
function photo(t) {
  t.colorSpace = THREE.SRGBColorSpace;
  t.generateMipmaps = false;
  t.minFilter = THREE.LinearFilter;
  t.magFilter = THREE.LinearFilter;
  t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
}
function data(t) {
  t.colorSpace = THREE.NoColorSpace;
  t.generateMipmaps = false;
  t.minFilter = THREE.NearestFilter;
  t.magFilter = THREE.NearestFilter;
  t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
}

// A PNG read back as pixels, for the CPU-side ray marching.
function loadPixels(url) {
  return new Promise(function (resolve, reject) {
    const img = new Image();
    img.onload = function () {
      try {
        const cv = document.createElement('canvas');
        cv.width = img.naturalWidth; cv.height = img.naturalHeight;
        const ctx = cv.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(img, 0, 0);
        resolve({ width: cv.width, height: cv.height, data: ctx.getImageData(0, 0, cv.width, cv.height).data });
      } catch (e) { reject(e); }
    };
    img.onerror = function () { reject(new Error(url + ' failed to load')); };
    img.src = url;
  });
}

export function preloadBallroom() {
  if (_promise) return _promise;
  _promise = fetch(MANIFEST_URL)
    .then(function (r) {
      if (!r.ok) throw new Error('ballroom.json HTTP ' + r.status);
      return r.json();
    })
    .then(function (manifest) {
      const parts = [
        loadTexture('textures/' + manifest.off, photo),
        loadTexture('textures/' + manifest.diff, photo),
        loadTexture('textures/' + manifest.ids, data),
        fetch('textures/' + manifest.data).then(function (r) {
          if (!r.ok) throw new Error(manifest.data + ' HTTP ' + r.status);
          return r.json();
        })
      ];
      if (manifest.depth) {
        // as GPU textures too: the photo pass reconstructs every pixel's
        // point in the room. Linear filtering is exact enough on the two-byte
        // split (both bytes interpolate linearly), no mipmaps, no colour space.
        const map = function (t) { t.colorSpace = THREE.NoColorSpace; t.generateMipmaps = false; t.minFilter = THREE.LinearFilter; t.magFilter = THREE.LinearFilter; t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping; };
        parts.push(loadTexture('textures/' + manifest.depth.file, map));
        parts.push(loadTexture('textures/' + manifest.depth.normal, map));
        parts.push(loadPixels('textures/' + manifest.depth.file).then(function (px) {
          // RG16: w / wMax = (R * 256 + G) / 65535
          const n = px.width * px.height, w = new Float32Array(n), wMax = manifest.depth.wMax || 1.25;
          for (let i = 0; i < n; i++) w[i] = (px.data[i * 4] * 256 + px.data[i * 4 + 1]) / 65535 * wMax;
          return { width: px.width, height: px.height, w: w };
        }));
        parts.push(loadPixels('textures/' + manifest.depth.normal).then(function (px) {
          const n = px.width * px.height, nx = new Float32Array(n * 3);
          for (let i = 0; i < n; i++) { nx[i * 3] = px.data[i * 4] / 127.5 - 1; nx[i * 3 + 1] = px.data[i * 4 + 1] / 127.5 - 1; nx[i * 3 + 2] = px.data[i * 4 + 2] / 127.5 - 1; }
          return { width: px.width, height: px.height, n: nx };
        }));
      }
      return Promise.all(parts).then(function (p) {
        _assets = { manifest: manifest, off: p[0], diff: p[1], ids: p[2], data: p[3], depthTex: p[4] || null, normalTex: p[5] || null, depth: p[6] || null, normal: p[7] || null };
        return _assets;
      });
    })
    .catch(function (err) {
      console.error('[party-mode] ballroom assets unavailable, Ballroom will not be offered', err);
      _assets = null;
      return null;
    });
  return _promise;
}

export function getBallroom() {
  return _assets;
}

// Node tests only: hand in assets without a fetch.
export function _setBallroomForTests(a) { _assets = a; _promise = Promise.resolve(a); }
